import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useState } from "react";
import styles from "./ColorField.module.css";
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
const HEX_SHORTHAND_PATTERN = /^#?([0-9a-fA-F]{3})$/;
const FALLBACK_SWATCH_COLOR = "#000000";
function normalizeHex(raw) {
    const trimmed = raw.trim();
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}
/**
 * Expande a forma curta (`#fff` → `#ffffff`), quando o texto for EXATAMENTE um hexadecimal de
 * 3 dígitos — nunca aplicado durante a digitação (ver `handleBlur`). Achado da revisão
 * adversarial: `adapters/branding/contrastSpecification.ts` (`parseHexColor`) sempre aceitou a
 * forma curta, enquanto este campo e `features/platform/branding.ts` só aceitavam seis dígitos
 * — a mesma cor podia passar na verificação de contraste e ser recusada aqui.
 *
 * Por que só no blur, e não a cada tecla: `value` é controlado pelo chamador (ex.:
 * `BrandingSection`), e o efeito abaixo resincroniza `draft` sempre que `value` muda. Ao digitar
 * "#123456" letra a letra, o prefixo "#123" já é um hexadecimal curto válido — expandir e
 * propagar NESSE instante disparava o efeito de resync com o valor errado ("#112233"),
 * atropelando o restante da digitação em andamento antes que ela terminasse (medido: o campo
 * nunca chegava a "#123456"). No blur não há mais digitação em andamento para atropelar.
 */
function expandShorthand(raw) {
    const withHash = normalizeHex(raw);
    const shorthand = HEX_SHORTHAND_PATTERN.exec(withHash);
    if (!shorthand?.[1])
        return null;
    const expanded = shorthand[1]
        .split("")
        .map((c) => c + c)
        .join("");
    return `#${expanded}`;
}
/**
 * Campo de cor de marca do Azim CRM (ColorField).
 * Encapsula o seletor de cor nativo — com os pseudo-elementos do controle
 * estilizados nos dois motores (DD-012) — sincronizado com um campo
 * hexadecimal validado. Um hexadecimal inválido nunca é propagado ao
 * chamador nem quebra o campo: fica sinalizado como erro até ser corrigido.
 */
export function ColorField({ label, value, onChange, status, id }) {
    const [draft, setDraft] = useState(value);
    const [invalid, setInvalid] = useState(false);
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    useEffect(() => {
        setDraft(value);
        setInvalid(false);
    }, [value]);
    const handlePickerChange = (e) => {
        const hex = e.target.value;
        setDraft(hex);
        setInvalid(false);
        onChange(hex);
    };
    const handleTextChange = (e) => {
        const raw = e.target.value;
        setDraft(raw);
        if (raw.trim() === "") {
            setInvalid(false);
            return;
        }
        const normalized = normalizeHex(raw);
        if (HEX_PATTERN.test(normalized)) {
            setInvalid(false);
            onChange(normalized);
        }
        else {
            // Hexadecimal inválido: sinaliza o erro, mas não propaga e não apaga o
            // que foi digitado — o chamador continua com o último valor válido. A forma curta de
            // 3 dígitos passa por aqui como "inválida" de propósito enquanto o campo tem foco —
            // ela só é aceita no blur (ver `expandShorthand`), porque ainda pode ser o prefixo de
            // um hexadecimal de 6 dígitos que o usuário não terminou de digitar.
            setInvalid(true);
        }
    };
    const handleBlur = () => {
        if (!invalid)
            return;
        const expanded = expandShorthand(draft);
        if (expanded && HEX_PATTERN.test(expanded)) {
            setDraft(expanded);
            setInvalid(false);
            onChange(expanded);
        }
    };
    const swatchValue = HEX_PATTERN.test(normalizeHex(value)) ? normalizeHex(value) : FALLBACK_SWATCH_COLOR;
    return (_jsxs("div", { className: styles.root, children: [_jsx("label", { htmlFor: fieldId, className: styles.label, children: label }), _jsxs("div", { className: styles.controls, children: [_jsx("input", { type: "color", className: styles.swatch, value: swatchValue, onChange: handlePickerChange, "aria-label": `Selecionar cor para ${label}` }), _jsx("input", { id: fieldId, type: "text", className: [styles.hexInput, invalid ? styles.hexInputError : ""].filter(Boolean).join(" "), value: draft, onChange: handleTextChange, onBlur: handleBlur, "aria-invalid": invalid || undefined, "aria-describedby": invalid ? `${fieldId}-error` : undefined, spellCheck: false, maxLength: 7, placeholder: "#369DA0" }), status && _jsx("div", { className: styles.status, children: status })] }), invalid && (_jsx("span", { id: `${fieldId}-error`, role: "alert", className: styles.error, children: "Use um hexadecimal v\u00E1lido, no formato #RRGGBB." }))] }));
}
//# sourceMappingURL=ColorField.js.map